import { useEffect, useState } from 'react';
import { listFormTemplatesForVisitFormTemplatesGet } from '../../services/api/sdk.gen';
import type { FormTemplateResponse } from '../../services/api/types.gen';

/**
 * The agency's whole active template library, for a visit form to match by QID.
 *
 * One request per form rather than one per field. A form renders many text
 * boxes and each would otherwise ask separately, while an agency's library is
 * tens of rows, so fetching it whole and indexing on the client is both fewer
 * requests and less data.
 *
 * A failure is deliberately silent: templates are a shortcut for typing, so a
 * form whose library did not load is still a working form with every field
 * editable by hand. Surfacing an error here would interrupt documentation to
 * report that a convenience is missing. The failure is logged for support.
 */
export function useAgencyFormTemplates(): FormTemplateResponse[] {
  const [templates, setTemplates] = useState<FormTemplateResponse[]>([]);

  useEffect(() => {
    let cancelled = false;

    listFormTemplatesForVisitFormTemplatesGet()
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error('Form template load failed', response.error);
          return;
        }
        setTemplates(response.data);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Form template load failed', err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return templates;
}
